import { type PropsWithChildren, useMemo, useRef } from 'react';

import FormContextProvider, { useFormContext } from '~/contexts/FormContext/FormContext';
import type { OnSubmit } from '~/contexts/FormContext/types';
import { type AddressWithNameAndLocation, isSameLocation } from '~/utils/address';
import type { SearchParams } from '~/utils/search/types';

export type SearchFormType = Omit<SearchParams, 'page'>;

type SearchFormContextProps = {
  params?: SearchFormType;
  onSubmit: OnSubmit<SearchFormType>;
  defaultSubmitted?: boolean;
};

export const SearchFormContextProvider = ({
  children,
  params,
  onSubmit,
  defaultSubmitted,
}: PropsWithChildren<SearchFormContextProps>) => {
  const submittedAddress = useRef<AddressWithNameAndLocation>();

  const handleSubmit: OnSubmit<SearchFormType> = values => {
    submittedAddress.current = values.address;
    return onSubmit(values);
  };

  // the search url only keeps a slugified city name, so the address parsed back from it is poorer than
  // the one that was submitted: keep the submitted one as long as both point at the same place
  const address = useMemo(
    () =>
      isSameLocation(submittedAddress.current?.location, params?.address?.location)
        ? submittedAddress.current
        : params?.address,
    [params?.address],
  );

  return (
    <FormContextProvider<SearchFormType>
      defaultValues={{
        address,
        job: params?.job,
        sort: params?.sort,
        type: params?.type,
        distance: params?.distance,
        experience: params?.experience,
      }}
      onSubmit={handleSubmit}
      defaultSubmitted={defaultSubmitted}
    >
      {children}
    </FormContextProvider>
  );
};

export const useSearchFormContext = () => useFormContext<SearchFormType>();
